import { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

/**
 * Theme palette — Brooklyn editorial system (Apr 24, 2026 site-wide migration).
 * Semantic keys are preserved (existing components consume `colors.warmStone` etc.)
 * but their values are remapped to the new ink/cream/gold palette.
 *
 * Source palette (mirrors tailwind.config.js):
 *   ink     #1c1510   ink.mid #1c1510
 *   cream   #faf7f2   warm    #f5f0e8   linen #ede8de
 *   gold    #a07830   gold.light #c49a45
 *   ash     #8a7d6e
 */
export const getThemeColors = (theme) => {
  if (theme === "dark") {
    return {
      // Backgrounds (deep editorial dark — warm, not cold black)
      warmStone: "#0f0a07",
      warmStoneHover: "#161009",
      softWhite: "#1c1510",
      inkNavy: "#0f0a07",
      inkNavyHover: "#1c1510",

      // Text — warm cream for dark mode (not pure white)
      inkGrey: "#f5f0e8",
      inkGreyMuted: "rgba(245,240,232,0.85)",
      inkGreyFaint: "rgba(245,240,232,0.65)",
      inkGreyLight: "rgba(245,240,232,0.55)",

      // Accents
      oliveSage: "#c49a45",
      oliveSageHover: "#d4b06a",
      mineralTaupe: "#8a7d6e",
      warmGold: "#c49a45",
      warmGoldHover: "#d4b06a",

      // Borders
      border: "rgba(160,120,48,0.28)",
      borderLight: "rgba(160,120,48,0.18)",

      // Special
      cardBg: "#1c1510",
      cardBgHover: "#1c1510",
      heroBg: "#0a0604",
      offWhite: "#1c1510",
    };
  }

  // Light mode — Brooklyn editorial palette (default)
  return {
    // Backgrounds
    warmStone: "#faf7f2",       // page background — cream
    warmStoneHover: "#ede8de",  // alt section background — linen
    softWhite: "#faf7f2",       // card background — cream
    inkNavy: "#1c1510",         // dark accent (was navy, now warm ink)
    inkNavyHover: "#1c1510",    // hover state for ink

    // Text — high contrast for WCAG AA on cream backgrounds
    inkGrey: "#1c1510",          // primary text — warm near-black
    inkGreyMuted: "#3d2e22",     // secondary text — warm dark
    inkGreyFaint: "#5a4a3a",     // tertiary text
    inkGreyLight: "#8a7d6e",     // ash — subtle/captions

    // Accents — gold replaces olive
    oliveSage: "#a07830",        // primary accent — bronze gold
    oliveSageHover: "#8a6628",
    mineralTaupe: "#8a7d6e",     // borders/subtle elements
    warmGold: "#a07830",
    warmGoldHover: "#c49a45",

    // Borders
    border: "rgba(28,21,16,0.18)",
    borderLight: "rgba(28,21,16,0.10)",

    // Special
    cardBg: "#faf7f2",
    cardBgHover: "#f5f0e8",
    heroBg: "#1c1510",
    offWhite: "#f5f0e8",
  };
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const getInitialTheme = () => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved;
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const colors = getThemeColors(theme);

  useEffect(() => {
    localStorage.setItem("theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
